import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import Select, { SelectRenderer } from "react-dropdown-select";
import { usersByGroupListName } from "src/api/config";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { ReturnItemsType } from "src/modules/modules.type";
import { getItemsRequest } from "src/modules/spItems/api";
import { getUserRequest } from "src/modules/users/api";
import { User } from "src/modules/users/types";
import { fieldType } from "src/types/form";
import { getNameAndSurname, nameFromEmail, prefix, removePrefix, shortNameFromEmail } from "src/utils/users.utils";
import { twMerge } from "tailwind-merge";
import { fetchSearchByKey, searchPeopleRequest } from "./form/peoplePicker/api";
import { SearchUI } from "./Search";
import { UserAvatarItem } from "./TGUserAvatarsList";
import { isStrapi } from "src/api/config/systemSwitch";
import Loading from "src/components/Loading";

export type Props<K = Option> = {
  dataField: fieldType;
  showSearch?: boolean;
  onChange: (data: K[]) => void;
  values: K[];
  errors?: any;
  fetchKey: string | string[];
  searchPlaceholder?: string;
  min?: number;
  clear?: boolean;
  list?: K[];
  excludeFilters?: boolean;
  classNames?: {
    selectClassName?: string;
    inputClassName?: string;
    contentClassName?: string;
    dropdownClassName?: string;
    labelClassName?: string;
    noticeClassName?: string;
  };
  callbackRemoveItem?: (data: K) => void;
  listLoading?: boolean;
  /**
   * Когда true — используется "объединённый" дизайн (как в peoplePicker):
   * выбранные элементы и поле ввода для поиска находятся вместе, в одной
   * строке, без отдельного окна поиска внутри выпадающего списка.
   */
  newDesign?: boolean;
  /**
   * Когда true — при полной очистке строки поиска выпадающий список
   * сворачивается до уже выбранных элементов (не показывает результаты
   * последнего поиска/весь список), чтобы не путать пользователя
   * "лишними" вариантами, которые он не искал. Выбранные элементы при
   * этом остаются видимыми в любом случае.
   * По умолчанию false — сохраняется прежнее поведение (полный список).
   */
  clearOptionsOnEmptySearch?: boolean;
};

export type Option = {
  id: string;
  name: string;
  disabled?: boolean;
  value?: any;
  isCorrect?: boolean;
  isRequired?: boolean;
  withAction?: boolean;
};

const getPeoplePickerPersonnel = (item?: Option | null): string => {
  const value = item?.value || {};
  const fromProfile =
    value.abiPersonnelNumber ||
    value.PersonalNumber ||
    value.UserProfileProperties?.find(
      (property: any) =>
        property?.Key === "abiPersonnelNumber" || property?.Key === "UserName" || property?.Key === "EmployeeNumber",
    )?.Value ||
    "";
  if (fromProfile) {
    return String(fromProfile);
  }

  const nameMatch = String(item?.name || value.DisplayName || value.Name || "").match(/\((\d{5,})\)\s*$/);
  return nameMatch?.[1] || "";
};

const getPeoplePickerDedupeKey = (item?: Option | null): string => {
  const value = item?.value || {};
  const email = (
    value.Email ||
    value.email ||
    value.EmailAddress ||
    value.WorkEmail ||
    (value.AccountName ? removePrefix(value.AccountName, prefix) : "") ||
    (typeof item?.id === "string" && item.id.includes("@") ? item.id : "")
  )
    .toString()
    .toLowerCase()
    .trim();

  if (email) {
    return `email:${email}`;
  }

  const personnel = getPeoplePickerPersonnel(item);
  if (personnel) {
    return `pers:${personnel}`;
  }

  return item?.id != null ? `id:${String(item.id)}` : "";
};

const isSamePeoplePickerOption = (left?: Option | null, right?: Option | null) => {
  const leftKey = getPeoplePickerDedupeKey(left);
  const rightKey = getPeoplePickerDedupeKey(right);
  if (leftKey && rightKey && leftKey === rightKey) {
    return true;
  }

  const leftPersonnel = getPeoplePickerPersonnel(left);
  const rightPersonnel = getPeoplePickerPersonnel(right);
  return Boolean(leftPersonnel) && leftPersonnel === rightPersonnel;
};

const uniquePeoplePickerOptions = (items: Option[] = []) => {
  const seen = new Set<string>();
  const seenPersonnel = new Set<string>();
  return items.filter((item) => {
    const key = getPeoplePickerDedupeKey(item);
    const personnel = getPeoplePickerPersonnel(item);
    if ((key && seen.has(key)) || (personnel && seenPersonnel.has(personnel))) {
      return false;
    }
    if (key) {
      seen.add(key);
    }
    if (personnel) {
      seenPersonnel.add(personnel);
    }
    if (key || personnel) {
      return true;
    }
    const fallback = `keep:${String(item?.id || "")}:${String(item?.name || "")}`;
    if (seen.has(fallback)) {
      return false;
    }
    seen.add(fallback);
    return true;
  });
};

const isNullLikePeoplePickerName = (name?: string | null) => {
  if (name === undefined || name === null) {
    return true;
  }
  const normalized = String(name).trim().toLowerCase();
  return !normalized || normalized === "null" || normalized === "undefined" || /^null\b/.test(normalized) || /\(\s*(undefined|null)\s*\)/.test(normalized);
};

const getPeoplePickerEmail = (option?: Option | null): string => {
  const value = option?.value;
  if (value?.AccountName) {
    return removePrefix(String(value.AccountName), prefix);
  }
  const email = value?.Email || value?.email || value?.EmailAddress || value?.WorkEmail || "";
  if (email) {
    return removePrefix(String(email), prefix);
  }
  if (typeof option?.id === "string" && option.id.includes("@")) {
    return removePrefix(option.id, prefix);
  }
  return "";
};

const isBlockedPeoplePickerOption = (option?: Option | null) => {
  const value = option?.value || {};
  return value.blocked === true || value.Blocked === true;
};

const peoplePickerSearchTokens = (option: Option): string[] => {
  const rawName = String(option?.name || option?.value?.DisplayName || option?.value?.Name || option?.value?.PreferredName || option?.value?.PreferredNameRus || "");
  const visibleName = rawName.replace(/\s*\((?:уволен|\d+|нет номера|undefined|null|не активен)\)\s*$/i, "");
  return [visibleName, getPeoplePickerEmail(option), getPeoplePickerPersonnel(option)]
    .join(" ")
    .toLowerCase()
    .split(/[\s@.|,()]+/)
    .filter((token) => token.length > 0);
};

const peoplePickerOptionMatchesQuery = (option: Option, query: string) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter((word) => word.length > 0);
  if (!words.length) {
    return false;
  }
  const tokens = peoplePickerSearchTokens(option);
  const blob = tokens.join(" ");
  if (blob.includes(query.trim().toLowerCase())) {
    return true;
  }
  // «Dmitry» должен находить «Dmitriy»: слово запроса совпадает с токеном имени
  // целиком, как подстрока или как начало токена.
  return words.every((word) =>
    tokens.some((token) => token.includes(word) || (word.length >= 3 && token.startsWith(word))),
  );
};

const getPeoplePickerChipName = (option: Option, short?: boolean) => {
  const email = getPeoplePickerEmail(option);
  const rawName = isNullLikePeoplePickerName(option.name)
    ? ""
    : String(option.name).replace(/\s*\((?:уволен|\d+|нет номера|undefined|null|не активен)\)\s*$/i, "").trim();
  const name = rawName
    ? getNameAndSurname(rawName, "", short)
    : short
      ? shortNameFromEmail(email)
      : nameFromEmail(email);
  const fallback = name || (short ? "ND" : email || "Пользователь");
  if (!short) {
    return `${fallback} (уволен)`;
  }
  return fallback;
};

export function TGSelectPicker(props: Props) {
  const { dataField, onChange, values, errors, fetchKey, searchPlaceholder = "", min = 1, classNames } = props;
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [defaultValues, setDefaultValue] = useState<Option[]>(values);
  const [searchString, setSearchString] = useState("");

  async function fetchData() {
    setLoading(true);
    try {
      if (searchString.length >= min) {
        if (Array.isArray(fetchKey)) {
          if (isStrapi) {
            const data = (await fetchSearchByKey(searchString, fetchKey[0])) as Option[];
            if (data) {
              setOptions(data);
            }
          } else {
            const dataPromises = fetchKey.map((str) => fetchSearchByKey(searchString, str));
            const data = await Promise.all(dataPromises);
            if (data.length) {
              const flatData = data.flatMap((d) => d);
              setOptions(flatData);
            }
          }
        } else {
          const data = (await fetchSearchByKey(searchString, fetchKey)) as Option[];
          if (data) {
            setOptions(data);
          }
        }
      } else {
        if (searchString.length === 0) setOptions([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [searchString]);

  const customContentRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return (
      <>
        {(state.values.length > 0 &&
          state.values.map((option) => (
            <span
              key={option.id}
              className={twMerge(
                "rounded p-1 flex flex-row items-center mr-2.5 bg-[#F3F4F6]",
                props.disabled ? "bg-[#E5E7EB]" : "",
                classNames?.contentClassName,
              )}
            >
              <span className={twMerge("block", props.disabled ? "mx-0" : "mx-2.5")}>{option.name}</span>
              {/* {!dataField.single && ( */}
              {props.disabled || !!dataField.hideRemoveButton ? null : (
                <span onClick={(e) => methods.removeItem(e, option, true)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20.707 4.70697L19.293 3.29297L12 10.586L4.70697 3.29297L3.29297 4.70697L10.586 12L3.29297 19.293L4.70697 20.707L12 13.414L19.293 20.707L20.707 19.293L13.414 12L20.707 4.70697Z"
                      fill="#9CA3AF"
                    />
                  </svg>
                </span>
              )}
              {/* )} */}
            </span>
          ))) || (
          <span className={twMerge("text-gray-400", classNames?.contentClassName)}>{dataField.placeholder}</span>
        )}
      </>
    );
  };

  const customDropdownRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return (
      <div className={twMerge("relative shadow-custom", classNames?.dropdownClassName)}>
        <div className="relative p-5 bg-white sticky-custom">
          <SearchUI
            placeholder={searchPlaceholder}
            defaultValue={searchString}
            onClick={(query) => setSearchString(query)}
            loading={loading}
          />
        </div>

        {state.search.length >= 3 && props.options.length === 0 && (
          <span>
            Нет совпадений по запросу <strong>{state.search}</strong>
          </span>
        )}
        {props.options.map((option) => {
          if (!props.keepSelectedInList && methods.isSelected(option)) {
            return null;
          }
          return (
            <div className="px-5 py-2 hover:bg-gray-400 hover:text-white" key={option.id}>
              {option.disabled ? (
                <div aria-disabled>{option.name}</div>
              ) : (
                <div
                  className="flex flex-row items-center space-x-2 item-renderer"
                  onClick={(e) => (option.disabled ? null : methods.addItem(option))}
                >
                  {!dataField.single && (
                    <input
                      className="h-5 w-5 rounded border-gray-300 text-[#012169]"
                      onChange={() => (option.disabled ? undefined : methods.addItem(option))}
                      type="checkbox"
                      checked={state.values.some((selectedOption) => selectedOption.id === option.id)}
                    />
                  )}
                  <div>{option.name}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const dropDownHandleRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return <ChevronDownIcon className={`${state.dropdown ? "rotate-180 transform" : ""} h-6 w-6 text-black`} />;
  };

  return (
    <div className={`${dataField.width} mb-5 ${(dataField.disabled && "cursor-not-allowed") || ""}`}>
      <label
        className={twMerge(
          "flex flex-row justify-between text-sm font-medium leading-6 text-gray-900 mb-2.5",
          classNames?.labelClassName,
        )}
      >
        <span>{dataField.title}</span>
        {dataField.require && (
          <div className="flex flex-row justify-between">
            {Object.keys(errors).length > 0 && errors[dataField.key] && (
              <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>
            )}
            <span className="text-[#BA0C2F]">*</span>
          </div>
        )}
      </label>
      <Select<Option>
        values={values || defaultValues}
        multi={!dataField.single}
        className={classNames?.selectClassName || ""}
        contentRenderer={customContentRenderer}
        dropdownRenderer={customDropdownRenderer}
        dropdownHandleRenderer={dropDownHandleRenderer}
        // optionRenderer={({ item, props, methods }) => (
        //   <FlexCol
        //     className={twMerge(
        //       props.multi ? "bg-[#F3F4F6] px-2.5 rounded-[4px]" : "",
        //       "text-black py-1 flex-nowrap mr-2.5 relative z-50"
        //     )}
        //   >
        //     <FlexRow className="items-center justify-between gap-x-2.5">
        //       <span className="flex flex-col text-black">{item.name}</span>

        //       <button onClick={(e) => methods.removeItem(e, item, false)} className="flex flex-col translate-y-[.5px]">
        //         <XMarkIcon width={24} height={24} color="#9CA3AF" />
        //       </button>
        //     </FlexRow>
        //   </FlexCol>
        // )}
        options={options}
        disabled={dataField.disabled}
        onChange={(value) => onChange(value)}
        labelField="name"
        valueField="id"
        noDataLabel="Нет данных"
      />
      {dataField.notice && (
        <div className={twMerge("text-sm text-[#6B7280]", classNames?.noticeClassName)}>{dataField.notice}</div>
      )}
    </div>
  );
}

export function TGSelectUserPickerByProperties(props: Props) {
  const {
    dataField,
    onChange,
    values,
    errors,
    fetchKey,
    searchPlaceholder = "",
    min = 1,
    classNames,
    callbackRemoveItem,
    excludeFilters,
    newDesign = false,
  } = props;
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [defaultValues, setDefaultValue] = useState<Option[]>(values);
  const [searchString, setSearchString] = useState("");
  // Храним актуальную строку поиска, чтобы игнорировать ответы от
  // "устаревших" запросов, которые могли завершиться позже более нового
  // (иначе после очистки поля можно увидеть результаты предыдущего поиска).
  const latestSearchRef = useRef("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Эффект поиска зависит только от строки запроса, поэтому выбранных
  // читаем из ref — иначе ответ может прийти со старым списком без уволенного.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const focusSearchInput = () => {
    // Select после открытия дропдауна может перехватить фокус,
    // поэтому ставим его в поле поиска на следующем кадре.
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  };

  const onSearch = ({ props, state }: SelectRenderer<Option>) => {
    if (state.search.length >= min) {
      if (state.search !== searchString) {
        setSearchString(state.search);
      }
    } else if (state.search.length === 0 && searchString !== "") {
      setSearchString("");
    }

    // Опции уже отфильтрованы на сервере (см. fetchData), поэтому
    // здесь не применяем дополнительную клиентскую фильтрацию.
    return props.options;
  };

  useEffect(() => {
    latestSearchRef.current = searchString;

    async function fetchData() {
      const currentSearch = searchString;
      setLoading(true);
      try {
        if (searchString.length >= min) {
          const data = await searchPeopleRequest(searchString, 10);

          let result: (User | null)[] = [];
          if (isStrapi) {
            let emails: string[] = [];
            data.forEach((d: any) => {
              emails = [...emails, removePrefix(d.AccountName, prefix) || d.WorkEmail];
            });
            result = await getUserRequest(emails);
            if (!excludeFilters) {
              result = result.filter((promise) => {
                if (promise && promise.AccountName && promise.DirectReports && promise.DirectReports.length) {
                  return true;
                }
                return false;
              });
            }
          } else {
            let promises: Promise<User | null>[] = [];

            data.forEach((d: any) =>
              promises.push(
                getUserRequest(
                  (removePrefix(d.AccountName, prefix) && [removePrefix(d.AccountName, prefix)]) || [d.WorkEmail],
                ),
              ),
            );

            let response = await Promise.all(promises);

            result = response.filter((promise) => {
              if (promise && promise.AccountName && promise.DirectReports && promise.DirectReports.length) {
                return true;
              }
              return false;
            });
          }

          if (result) {
            // Игнорируем ответ, если пока он выполнялся, пользователь уже
            // изменил (в т.ч. очистил) строку поиска — иначе можно
            // "затереть" актуальное состояние списка устаревшим ответом.
            if (latestSearchRef.current !== currentSearch) return;

            const nameAndIdValues = result.map((d) => ({
              id: removePrefix(d!.AccountName, prefix),
              name: d!.DisplayName
                ? getNameAndSurname(d!.DisplayName, d!.Email)
                : nameFromEmail(removePrefix(d!.AccountName, prefix)),
              value: d,
            }));

            // Сначала совпадения среди уже выбранных (включая уволенных:
            // справочник их не возвращает из-за blocked: false).
            // Затем пользователи из системы. Если это один и тот же человек,
            // оставляем запись из выбранных — иначе поиск затирает её системной.
            const selectedMatches = (valuesRef.current || []).filter((item) =>
              peoplePickerOptionMatchesQuery(item, currentSearch),
            );
            const systemOnly = nameAndIdValues.filter(
              (item) => !selectedMatches.some((selected) => isSamePeoplePickerOption(selected, item)),
            );

            setOptions(uniquePeoplePickerOptions([...selectedMatches, ...systemOnly]));
          }
        } else {
          if (searchString.length === 0) {
            // При очистке поиска список результатов сбрасывается, но
            // уже выбранные элементы должны оставаться видимыми в любом
            // случае — иначе пользователь теряет из виду свой выбор.
            setOptions(valuesRef.current);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [searchString]);

  const customContentRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return (
      <>
        {(state.values.length > 0 &&
          state.values.map((option) => {
            const userEmail = getPeoplePickerEmail(option);
            const blocked = isBlockedPeoplePickerOption(option);
            const name = blocked
              ? getPeoplePickerChipName(option)
              : option.name && !isNullLikePeoplePickerName(option.name)
                ? getNameAndSurname(option.name, "")
                : nameFromEmail(userEmail);
            const shortName = blocked
              ? getPeoplePickerChipName(option, true)
              : option.name && !isNullLikePeoplePickerName(option.name)
                ? getNameAndSurname(option.name, "", true)
                : shortNameFromEmail(userEmail);

            return (
              <span
                key={option.id}
                className={twMerge(
                  "rounded p-1 flex flex-row items-center mr-2.5 bg-[#F3F4F6]",
                  props.disabled ? "bg-[#E5E7EB]" : "",
                  classNames?.contentClassName,
                )}
              >
                <span className={twMerge("block", props.disabled ? "mx-0" : "mx-2.5")}>
                  <UserAvatarItem
                    single={true}
                    item={{
                      name: name,
                      shortName: shortName,
                      email: blocked ? undefined : userEmail,
                    }}
                  />
                </span>
                {props.disabled || !!dataField.hideRemoveButton ? null : (
                  <span
                    onClick={(e) => {
                      methods.removeItem(e, option, true);

                      if (callbackRemoveItem) {
                        callbackRemoveItem(option);
                      }
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20.707 4.70697L19.293 3.29297L12 10.586L4.70697 3.29297L3.29297 4.70697L10.586 12L3.29297 19.293L4.70697 20.707L12 13.414L19.293 20.707L20.707 19.293L13.414 12L20.707 4.70697Z"
                        fill="#9CA3AF"
                      />
                    </svg>
                  </span>
                )}
              </span>
            );
          })) || (
          (!newDesign && (
            <span className={twMerge("text-gray-400", classNames?.contentClassName)}>{dataField.placeholder}</span>
          ))
        )}
        {newDesign && (
          <input
            value={state.search || ""}
            onChange={(e) => methods.setSearch(e)}
            placeholder={state.values.length > 0 ? "" : dataField.placeholder || searchPlaceholder}
            className="p-0 flex-1 min-w-[120px] border-0 outline-none bg-transparent focus:ring-0 focus:border-transparent placeholder:text-[#9CA3AF] sm:text-base sm:leading-6"
          />
        )}
      </>
    );
  };

  const customDropdownRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return (
      <div
        className={twMerge(
          "relative p-5 shadow-custom",
          newDesign ? "" : "pt-0",
          classNames?.dropdownClassName,
        )}
      >
        {!newDesign && (
          <div className="relative pt-5 mb-5 bg-white sticky-custom z-[1]">
            <SearchUI
              ref={searchInputRef}
              autoFocus
              placeholder={searchPlaceholder}
              defaultValue={searchString}
              onClick={(query) => setSearchString(query)}
              loading={loading}
            />
          </div>
        )}

        {loading && newDesign && (
          <div className="flex justify-center items-center mb-3">
            <Loading />
          </div>
        )}

        {state.search.length >= 3 && props.options.length === 0 && (
          <span>
            Нет совпадений по запросу <strong>{state.search}</strong>
          </span>
        )}
        {props.options.map((option) => {
          if (!props.keepSelectedInList && methods.isSelected(option)) {
            return null;
          }

          if (searchString && !peoplePickerOptionMatchesQuery(option, searchString)) {
            return null;
          }

          const userEmail = getPeoplePickerEmail(option);
          const blocked = isBlockedPeoplePickerOption(option);
          const name = blocked
            ? getPeoplePickerChipName(option)
            : option.name && !isNullLikePeoplePickerName(option.name)
              ? getNameAndSurname(option.name, "")
              : nameFromEmail(userEmail);
          const shortName = blocked
            ? getPeoplePickerChipName(option, true)
            : option.name && !isNullLikePeoplePickerName(option.name)
              ? getNameAndSurname(option.name, "", true)
              : shortNameFromEmail(userEmail);
          const isSelected = state.values.some((selectedOption) => isSamePeoplePickerOption(selectedOption, option));
          const toggleOption = () => {
            if (option.disabled) {
              return;
            }
            // addItem у уже выбранного снимает выбор. Проверка документов
            // нужна в тот же момент, что и при крестике у чипа.
            methods.addItem(option);
            if (isSelected && callbackRemoveItem) {
              callbackRemoveItem(option);
            }
          };

          return (
            <div className="mb-2" key={option.id}>
              {option.disabled ? (
                <div aria-disabled>{option.name}</div>
              ) : (
                <div
                  className="flex flex-row items-center space-x-2 item-renderer"
                  onClick={() => toggleOption()}
                >
                  {!dataField.single && (
                    <input
                      className="h-5 w-5 rounded border-gray-300 text-[#012169] pointer-events-none"
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                    />
                  )}
                  <div>
                    <UserAvatarItem
                      single={true}
                      item={{
                        name: name,
                        shortName: shortName,
                        email: blocked ? undefined : userEmail,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const dropDownHandleRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return <ChevronDownIcon className={`${state.dropdown ? "rotate-180 transform" : ""} h-6 w-6 text-black`} />;
  };

  return (
    <div className={`${dataField.width} mb-5 ${(dataField.disabled && "cursor-not-allowed") || ""}`}>
      <label
        className={twMerge(
          "flex flex-row justify-between text-sm font-medium leading-6 text-gray-900 mb-2.5",
          classNames?.labelClassName,
        )}
      >
        <span>{dataField.title}</span>
        {dataField.require && (
          <div className="flex flex-row justify-between">
            {Object.keys(errors).length > 0 && errors[dataField.key] && (
              <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>
            )}
            <span className="text-[#BA0C2F]">*</span>
          </div>
        )}
      </label>
      <Select<Option>
        values={values || defaultValues}
        multi={!dataField.single}
        className={classNames?.selectClassName || ""}
        contentRenderer={customContentRenderer}
        dropdownRenderer={customDropdownRenderer}
        dropdownHandleRenderer={dropDownHandleRenderer}
        onDropdownOpen={focusSearchInput}
        // optionRenderer={({ item, props, methods }) => (
        //   <FlexCol
        //     className={twMerge(
        //       props.multi ? "bg-[#F3F4F6] px-2.5 rounded-[4px]" : "",
        //       "text-black py-1 flex-nowrap mr-2.5 relative z-50"
        //     )}
        //   >
        //     <FlexRow className="items-center justify-between gap-x-2.5">
        //       <span className="flex flex-col text-black">{item.name}</span>

        //       <button onClick={(e) => methods.removeItem(e, item, false)} className="flex flex-col translate-y-[.5px]">
        //         <XMarkIcon width={24} height={24} color="#9CA3AF" />
        //       </button>
        //     </FlexRow>
        //   </FlexCol>
        // )}
        options={options}
        disabled={dataField.disabled}
        onChange={(value) => onChange(uniquePeoplePickerOptions(value))}
        searchFn={newDesign ? onSearch : undefined}
        // При newDesign не даём библиотеке самой закрывать дропдаун и
        // очищать введённый текст поиска при выборе/снятии элемента —
        // это должно оставаться под контролем пользователя.
        closeOnSelect={newDesign ? false : undefined}
        clearOnSelect={newDesign ? false : undefined}
        labelField="name"
        valueField="id"
        noDataLabel="Нет данных"
      />
      {dataField.notice && (
        <div className={twMerge("text-sm text-[#6B7280]", classNames?.noticeClassName)}>{dataField.notice}</div>
      )}
    </div>
  );
}

export function TGSelectUserPickerByGroup(props: Props) {
  const {
    dataField,
    onChange,
    values,
    errors,
    fetchKey,
    searchPlaceholder = "",
    clear = false,
    min = 1,
    classNames,
  } = props;
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [defaultValues, setDefaultValue] = useState<Option[]>(values);
  const [searchString, setSearchString] = useState("");
  // const [clearActive, setClearActive] = useState(clear);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        if (searchString.length >= min) {
          let promise: Promise<ReturnItemsType<typeof usersByGroupListName>>;

          if (searchString.length === 0) {
            promise = getItemsRequest({
              listName: usersByGroupListName,
            });
          } else {
            promise = getItemsRequest({
              listName: usersByGroupListName,
              filters: { Title: searchString + "*" },
            });
          }

          const { items } = await promise;

          if (items) {
            const mappedGroupsResponse = items.map((item) => ({
              id: item.ID.toString(),
              name: item.Title,
              value: item.Users.split(", "),
            }));

            setOptions(mappedGroupsResponse);
          }
        } else {
          if (searchString.length === 0) {
            // setClearActive(false);
            setOptions([]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [searchString]);

  const customContentRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return (
      <>
        {(state.values.length > 0 &&
          state.values.map((option) => (
            <span
              key={option.id}
              className={twMerge(
                "rounded p-1 flex flex-row items-center mr-2.5 bg-[#F3F4F6]",
                option.disabled ? "bg-[#E5E7EB]" : "",
                classNames?.contentClassName,
              )}
            >
              <span className="block mx-2.5">{option.name}</span>

              {!!dataField.hideRemoveButton ? null : (
                <span onClick={(e) => methods.removeItem(e, option, false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20.707 4.70697L19.293 3.29297L12 10.586L4.70697 3.29297L3.29297 4.70697L10.586 12L3.29297 19.293L4.70697 20.707L12 13.414L19.293 20.707L20.707 19.293L13.414 12L20.707 4.70697Z"
                      fill="#9CA3AF"
                    />
                  </svg>
                </span>
              )}
            </span>
          ))) || (
          <span className={twMerge("text-gray-400", classNames?.contentClassName)}>{dataField.placeholder}</span>
        )}
      </>
    );
  };

  const customDropdownRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return (
      <div className={twMerge("relative p-5 pt-0 shadow-custom", classNames?.dropdownClassName)}>
        <div className="relative pt-5 mb-5 bg-white sticky-custom">
          <SearchUI
            placeholder={searchPlaceholder}
            defaultValue={searchString}
            onClick={(query) => setSearchString(query)}
            loading={loading}
          />
        </div>

        {state.search.length >= 3 && props.options.length === 0 && (
          <span>
            Нет совпадений по запросу <strong>{state.search}</strong>
          </span>
        )}
        {props.options.map((option) => {
          if (!props.keepSelectedInList && methods.isSelected(option)) {
            return null;
          }
          return (
            <div className="mb-2" key={option.id}>
              {option.disabled ? (
                <div aria-disabled>{option.name}</div>
              ) : (
                <div
                  className="flex flex-row items-center space-x-2 item-renderer"
                  onClick={(e) => (option.disabled ? null : methods.addItem(option))}
                >
                  {!dataField.single && (
                    <input
                      className="h-5 w-5 rounded border-gray-300 text-[#012169]"
                      onChange={() => (option.disabled ? undefined : methods.addItem(option))}
                      type="checkbox"
                      checked={state.values.some((selectedOption) => selectedOption.id === option.id)}
                    />
                  )}
                  <div>{option.name}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const dropDownHandleRenderer = ({ props, state, methods }: SelectRenderer<Option>) => {
    return <ChevronDownIcon className={`${state.dropdown ? "rotate-180 transform" : ""} h-6 w-6 text-black`} />;
  };

  return (
    <div className={`${dataField.width} mb-5 ${(dataField.disabled && "cursor-not-allowed") || ""}`}>
      <label
        className={twMerge(
          "flex flex-row justify-between text-sm font-medium leading-6 text-gray-900 mb-2.5",
          classNames?.labelClassName,
        )}
      >
        <span>{dataField.title}</span>
        {dataField.require && (
          <div className="flex flex-row justify-between">
            {Object.keys(errors).length > 0 && errors[dataField.key] && (
              <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>
            )}
            <span className="text-[#BA0C2F]">*</span>
          </div>
        )}
      </label>
      <Select<Option>
        values={values || defaultValues}
        multi={!dataField.single}
        className={classNames?.selectClassName || ""}
        contentRenderer={customContentRenderer}
        dropdownRenderer={customDropdownRenderer}
        dropdownHandleRenderer={dropDownHandleRenderer}
        options={options}
        disabled={dataField.disabled}
        onChange={(value) => onChange(value)}
        labelField="name"
        valueField="id"
        noDataLabel="Нет данных"
      />
      {dataField.notice && (
        <div className={twMerge("text-sm text-[#6B7280]", classNames?.noticeClassName)}>{dataField.notice}</div>
      )}
    </div>
  );
}

export function TGSelectPickerByList<T extends Option>(props: Props<T>) {
  const {
    dataField,
    onChange,
    values,
    errors,
    fetchKey,
    searchPlaceholder = "",
    min = 1,
    classNames,
    list,
    listLoading = false,
    showSearch = false,
    newDesign = false,
    clearOptionsOnEmptySearch = false,
  } = props;
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [defaultValues, setDefaultValue] = useState<T[]>(values);
  const [searchString, setSearchString] = useState("");

  const onSearch = ({ props, state }: SelectRenderer<T>) => {
    if (state.search.length >= min) {
      if (state.search !== searchString) {
        setSearchString(state.search);
      }
    } else if (state.search.length === 0 && searchString !== "") {
      setSearchString("");
    }

    // Опции уже отфильтрованы (см. useAsyncEffect), поэтому здесь не
    // применяем дополнительную клиентскую фильтрацию.
    return props.options;
  };

  useAsyncEffect(async () => {
    setLoading(true);
    try {
      if (list && searchString.length >= min) {
        const data = list.filter((item) => item.name.toLowerCase().includes(searchString.toLowerCase()));

        if (data) {
          setOptions(data);
        }
      } else {
        if (searchString.length === 0) {
          // При очистке поиска либо показываем весь список (по умолчанию),
          // либо, если включён clearOptionsOnEmptySearch, сворачиваем его
          // до уже выбранных элементов — но не полностью пустым, чтобы
          // пользователь видел свой текущий выбор.
          setOptions(clearOptionsOnEmptySearch ? values : list || []);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [searchString]);

  useEffect(() => {
    if (list && list?.length > 0) {
      setOptions(list);
    }
  }, [list]);

  const customContentRenderer = ({ props, state, methods }: SelectRenderer<T>) => {
    return (
      <>
        {(state.values.length > 0 &&
          state.values.map((option) => {
            return (
              <span
                key={option.id}
                className={twMerge(
                  "rounded p-1 flex flex-row items-center mr-2.5 bg-[#F3F4F6]",
                  props.disabled ? "bg-[#E5E7EB]" : "",
                  classNames?.contentClassName,
                )}
              >
                <span className={twMerge("block", props.disabled ? "mx-0" : "mx-2.5")}>{option.name}</span>
                {/* {!dataField.single && ( */}
                {props.disabled || !!dataField.hideRemoveButton ? null : (
                  <span onClick={(e) => methods.removeItem(e, option, true)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20.707 4.70697L19.293 3.29297L12 10.586L4.70697 3.29297L3.29297 4.70697L10.586 12L3.29297 19.293L4.70697 20.707L12 13.414L19.293 20.707L20.707 19.293L13.414 12L20.707 4.70697Z"
                        fill="#9CA3AF"
                      />
                    </svg>
                  </span>
                )}
              </span>
            );
          })) || (
          (!newDesign && (
            <span className={twMerge("text-gray-400", classNames?.contentClassName)}>{dataField.placeholder}</span>
          ))
        )}
        {newDesign && (
          <input
            value={state.search || ""}
            onChange={(e) => methods.setSearch(e)}
            placeholder={state.values.length > 0 ? "" : dataField.placeholder || searchPlaceholder}
            className="p-0 flex-1 min-w-[120px] border-0 outline-none bg-transparent focus:ring-0 focus:border-transparent placeholder:text-[#9CA3AF] sm:text-base sm:leading-6"
          />
        )}
      </>
    );
  };

  const customDropdownRenderer = ({ props, state, methods }: SelectRenderer<T>) => {
    return (
      <div className={twMerge("relative shadow-custom", classNames?.dropdownClassName)}>
        {!newDesign && (!list || showSearch) && (
          <div className="relative p-5 bg-white sticky-custom">
            <SearchUI
              placeholder={searchPlaceholder}
              defaultValue={searchString}
              onClick={(query) => setSearchString(query)}
              loading={loading}
            />
          </div>
        )}

        {loading && newDesign && (
          <div className="flex justify-center items-center p-5">
            <Loading />
          </div>
        )}

        {state.search.length >= 3 && props.options.length === 0 && (
          <span className="p-5">
            Нет совпадений по запросу <strong>{state.search}</strong>
          </span>
        )}
        {listLoading ? (
          <div className="flex justify-center items-center p-5">
            <Loading />
          </div>
        ) : (
          props.options.map((option) => {
            if (!props.keepSelectedInList && methods.isSelected(option)) {
              return null;
            }

            const isSelected = methods.isSelected(option);

            return (
              <div
                className={twMerge(
                  "px-5 py-2 hover:bg-gray-400 hover:text-white",
                  isSelected && "bg-gray-400 text-white",
                )}
                key={option.id}
              >
                {option.disabled ? (
                  <div aria-disabled>{option.name}</div>
                ) : (
                  <div
                    className="flex flex-row items-center space-x-2 item-renderer"
                    onClick={(e) => (option.disabled ? null : methods.addItem(option))}
                  >
                    {!dataField.single && (
                      <input
                        className="h-5 w-5 rounded border-gray-300 text-[#012169]"
                        onChange={() => (option.disabled ? undefined : methods.addItem(option))}
                        type="checkbox"
                        checked={state.values.some((selectedOption) => selectedOption.id === option.id)}
                      />
                    )}
                    <div>{option.name}</div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  const dropDownHandleRenderer = ({ props, state, methods }: SelectRenderer<T>) => {
    return <ChevronDownIcon className={`${state.dropdown ? "rotate-180 transform" : ""} h-6 w-6 text-black`} />;
  };
  return (
    <div className={`${dataField.width} mb-5 ${(dataField.disabled && "cursor-not-allowed") || ""}`}>
      <label
        className={twMerge(
          "flex flex-row justify-between text-sm font-medium leading-6 text-gray-900 mb-2.5",
          classNames?.labelClassName,
        )}
      >
        <span>{dataField.title}</span>
        {dataField.require && (
          <div className="flex flex-row justify-between">
            {Object.keys(errors).length > 0 && errors[dataField.key] && (
              <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>
            )}
            <span className="text-[#BA0C2F]">*</span>
          </div>
        )}
      </label>
      <Select<T>
        values={values || defaultValues}
        multi={!dataField.single}
        className={classNames?.selectClassName || ""}
        contentRenderer={customContentRenderer}
        dropdownRenderer={customDropdownRenderer}
        dropdownHandleRenderer={dropDownHandleRenderer}
        searchFn={newDesign ? onSearch : undefined}
        // При newDesign не даём библиотеке самой закрывать дропдаун и
        // очищать введённый текст поиска при выборе/снятии элемента —
        // это должно оставаться под контролем пользователя.
        closeOnSelect={newDesign ? false : undefined}
        clearOnSelect={newDesign ? false : undefined}
        // optionRenderer={({ item, props, methods }) => (
        //   <FlexCol
        //     className={twMerge(
        //       props.multi ? "bg-[#F3F4F6] px-2.5 rounded-[4px]" : "",
        //       "text-black py-1 flex-nowrap mr-2.5 relative z-50"
        //     )}
        //   >
        //     <FlexRow className="items-center justify-between gap-x-2.5">
        //       <span className="flex flex-col text-black">{item.name}</span>

        //       <button onClick={(e) => methods.removeItem(e, item, false)} className="flex flex-col translate-y-[.5px]">
        //         <XMarkIcon width={24} height={24} color="#9CA3AF" />
        //       </button>
        //     </FlexRow>
        //   </FlexCol>
        // )}
        options={options}
        disabled={dataField.disabled}
        onChange={(value) => onChange(value)}
        labelField="name"
        valueField="id"
        noDataLabel="Нет данных"
      />
      {dataField.notice && (
        <div className={twMerge("text-sm text-[#6B7280]", classNames?.noticeClassName)}>{dataField.notice}</div>
      )}
    </div>
  );
}
